<template>
  <div :class="['goal-update-editor', focused ? '-focused' : '', loading ? '-loading' : '']">
    <comment-layout
      :creator="update === null ? loggedInUser : update.creator"
      :hide-title="update === null"
      :timestamp="timestamp"
      :generated="update === null ? false : update.generated"
      :show-actions="!readOnly && (showMenu || showReactionBtn)"
      show-meta
    >
      <template #post-title>
        <m-tooltip
          v-if="hasCustomCreatedAt"
          class="_tooltip"
        >
          <m-icon
            class="_icon-info"
            type="clock-circle"
            :color="$colors.grey.lighten1"
            size="13"
          />
          <template #title>
            {{ $t('goalUpdateEditor.createdAtExplanation', { date: formatDate(update.createdAt) }) }}
          </template>
        </m-tooltip>
        <m-tag
          v-if="update !== null && update.type === updateType.checkin"
          xxs
          color="light"
          :style="{ textTransform: 'uppercase', fontWeight: '500' }"
          :title="$t('updateFeedItem.viaCheckin')"
          :clickable="!readOnly"
          @click="viewCheckIn"
        />
      </template>
      <div class="_content">
        <div
          v-if="allowComments"
          class="_comment-container"
        >
          <m-editor
            v-show="!(disabled && content === null && initialContent === null)"
            ref="editor"
            :key="editorKey"
            v-model:value="content"
            class="_editor"
            :placeholder="$t('goalUpdateEditor.writeComment')"
            :allowed-content="allowedContent"
            :disabled="disabled"
            :ctrl-enter-handlers="[onCmdOrModEnter]"
            :mod-enter-handlers="[onCmdOrModEnter]"
            :initial-value="initialContent"
            @focus="focused = true"
            @blur="blur"
          />
          <div
            v-if="showSubmit"
            class="_submit"
          >
            <m-btn
              color="primary"
              icon="send"
              round
              xs
              fab
              :disabled="!messageDirty || (update === null && messageEmpty)"
              :loading="loading"
              @click="submit"
            />
          </div>
        </div>
        <div
          v-if="allowGoalActivity"
          class="_activity-container"
        >
          <goal-activity-form
            v-for="(activity, index) in goalActivities"
            :key="`${index}_${activity.goal.uid}`"
            :activity="activity"
            class="_activity"
            :disabled="disabled"
            :read-only="readOnly"
            @change="updateActivity($event, index)"
          />
          <div
            v-if="showSubmit"
            class="_submit"
          >
            <m-btn
              color="primary"
              small
              :loading="loading"
              @click="submit"
            >
              <template v-if="update === null">
                {{ $t('general.send') }}
              </template>
              <template v-else>
                {{ $t('general.save') }}
              </template>
            </m-btn>
          </div>
        </div>
      </div>
      <template
        v-if="disabled && !readOnly && update !== null && update.type !== updateType.checkin && !isPrint"
        #actions
      >
        <m-card
          :level="1"
          no-padding
          border-radius="small"
        >
          <m-btn-group>
            <m-tooltip>
              <add-reaction-btn
                ref="reactionBtn"
                :update="update"
                fab
                light
                small
                hide-border
                @show="showReactionBtn = true"
                @hide="showReactionBtn = false"
              />
              <template #title>
                {{ $t('goalUpdateEditor.addReaction') }}
              </template>
            </m-tooltip>
            <m-tooltip>
              <m-btn
                icon="message"
                hide-border
                light
                small
                fab
                @click="$refs.comments.add()"
              />
              <template #title>
                {{ $t('goalUpdateEditor.replyInThread') }}
              </template>
            </m-tooltip>
            <m-dropdown
              v-if="menuItems.length > 0"
              v-model:value="showMenu"
              :title="$t('general.actions')"
              placement="bottomRight"
            >
              <m-tooltip>
                <m-btn
                  icon="ellipsis"
                  hide-border
                  small
                  light
                  fab
                  class="_link"
                  @click="showMenu = true"
                />
                <template #title>
                  {{ $t('general.moreActions') }}
                </template>
              </m-tooltip>
              <template #overlay>
                <m-card
                  no-padding
                  list
                >
                  <m-card-item
                    v-for="item in menuItems"
                    :key="item.name"
                    :icon="item.icon"
                    :style="item.style === undefined ? null : item.style"
                    @click="item.onClick"
                  >
                    {{ item.name }}
                  </m-card-item>
                </m-card>
              </template>
            </m-dropdown>
          </m-btn-group>
        </m-card>
      </template>
    </comment-layout>
    <div
      v-if="update !== null && update.type !== updateType.checkin"
      class="_bottom-actions"
    >
      <reaction-list
        :update="update"
        class="_reactions"
        :read-only="readOnly"
      />
      <comment-list
        ref="comments"
        :comments="comments"
        :update="update"
        class="_comments"
        collapse
      />
    </div>
    <m-dialog
      v-model:value="showCheckinModal"
      :max-width="$modalSizes.xl"
      hide-footer
      no-padding
      keep-height
      hide-header
    >
      <update-editor
        :update="update"
        disabled
        :modal="true"
      />
    </m-dialog>
  </div>
</template>

<script>
import AddReactionBtn from '@/components/reaction/AddReactionBtn.vue';
import CommentLayout from '@/components/comment/CommentLayout.vue';
import CommentList from '@/components/comment/CommentList.vue';
import GoalActivityForm from '@/components/goal/GoalActivityForm.vue';
import MEditor from '@/components/editor/MEditor.vue';
import ReactionList from '@/components/reaction/ReactionList.vue';
import UpdateEditor from '@/components/updates/UpdateEditor.vue';
import isEqual from 'lodash-es/isEqual';
import useAccess from '@/composables/access/access';
import useComments from '@/composables/comments/comments';
import useGoalProperty from '@/composables/property/goal-property';
import useGoals from '@/composables/goal/goals';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import usePrintMode from '@/composables/export/print-mode';
import useStatusProperty from '@/composables/goal/status-property';
import useUpdates from '@/composables/updates/updates';
import { DateTime } from 'luxon';
import { accessPolicyType,
  editorNodeType,
  featureFlag,
  updateType } from 'shared/constants.json';
import { copy } from 'shared/lib/copy';
import { current } from '@/lib/goal/progress';
import { isZero, trimEmptyLines } from '@/lib/editor/editor';
import { ref } from 'vue';

export default {
  name: 'GoalUpdateEditor',
  props: {
    update: {
      type: Object,
      default: () => null,
    },
    goal: {
      type: Object,
      required: true,
    },
    goalChildren: {
      type: Array,
      default: () => [],
    },
    allowComments: {
      type: Boolean,
      default: false,
    },
    allowGoalActivity: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    initialDisabled: {
      type: Boolean,
      default: false,
    },
    autoAddGoalActivity: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['created', 'saved', 'deleted'],
  components: { AddReactionBtn, GoalActivityForm, MEditor, UpdateEditor, ReactionList, CommentList, CommentLayout },
  setup(props) {
    const { comments } = useComments();
    const updateComments = props.update === null ? ref([]) : comments({ updateId: props.update.uid });
    const { isPrint } = usePrintMode();
    const updateSvc = useUpdates();

    const { selectSingle } = useGoals();

    const { statusProperty: statusProp } = useGoalProperty();
    const { loggedInUser, userLang } = useLoggedInUser();
    const { calculateInitialPropertyStatus } = useStatusProperty(statusProp, userLang.value);

    const allowedContent = [
      editorNodeType.codeBlock,
      editorNodeType.blockquote,
      editorNodeType.bulletList,
      editorNodeType.orderedList,
      editorNodeType.todoList,
      editorNodeType.mention,
      editorNodeType.goalMention,
    ];

    const { accountHasFeature } = useAccess();
    if (accountHasFeature([featureFlag.fileUpload])) {
      allowedContent.push(editorNodeType.image, editorNodeType.file);
    }

    return {
      selectSingle,
      comments: updateComments,

      createUpdate: updateSvc.createUpdate,
      updateUpdate: updateSvc.updateUpdate,
      deleteUpdate: updateSvc.deleteUpdate,
      fetchUpdatesForGoal: updateSvc.fetchUpdatesForGoal,
      createUpdateLoading: updateSvc.createUpdateLoading,
      updateUpdateLoading: updateSvc.updateUpdateLoading,
      userLang,
      loggedInUser,
      calculateInitialPropertyStatus,
      allowedContent,
      isPrint,
    };
  },
  data() {
    return {
      updateType,
      disabled: true,
      focused: false,
      initialContent: null,
      content: null,
      editorKey: 0,
      activitiesToCreate: [],
      showCheckinModal: false,
      showMenu: false,
      showReactionBtn: false,
    };
  },
  computed: {
    loading() {
      return this.updateUpdateLoading || this.createUpdateLoading;
    },
    timestamp() {
      if (this.update === null) {
        return '';
      }

      if (this.update.createdAt !== this.update.modifiedAt) {
        return `${this.formatDate(this.update.customCreatedAt)} (${this.$t('goalUpdateEditor.edited')})`;
      }

      return this.formatDate(this.update.customCreatedAt);
    },
    hasCustomCreatedAt() {
      if (this.update === null) {
        return false;
      }

      const d1 = DateTime.fromISO(this.update.createdAt);
      const d2 = DateTime.fromISO(this.update.customCreatedAt);

      return Math.abs(d1.diff(d2).as('day')) >= 1;
    },
    menuItems() {
      if (this.update.generated === true && [accessPolicyType.write, accessPolicyType.full].includes(this.goal.accessRight)) {
        return [
          {
            icon: 'delete',
            name: this.$t('general.delete'),
            onClick: this.onDeleteUpdate,
          },
        ];
      }

      if (this.update.creator === null || this.update.creator.uid !== this.loggedInUser.uid) {
        return [];
      }

      return [
        {
          icon: 'edit',
          name: this.$t('general.edit'),
          onClick: this.editUpdate,
        },
        {
          icon: 'delete',
          name: this.$t('general.delete'),
          onClick: this.onDeleteUpdate,
        },
      ];
    },
    messageDirty() {
      return !isEqual(this.content, this.initialContent);
    },
    messageEmpty() {
      return this.content === null || isZero(this.content);
    },
    goalActivities() {
      const ids = [this.goal.uid, ...this.goalChildren.map((g) => g.uid)];
      return this.activitiesToCreate.filter((a) => {
        if (a.referenced === true) {
          return false;
        }
        if (a.deletedAt !== undefined) {
          return false;
        }
        return ids.includes(a.goal.uid);
      });
    },
    showSubmit() {
      if (this.disabled) {
        return false;
      }
      if (this.allowComments) {
        return this.focused || this.messageDirty;
      }
      return this.allowGoalActivity;
    },
  },
  methods: {
    formatDate(date) {
      return DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_MED);
    },
    onCmdOrModEnter() {
      if (this.messageDirty) {
        this.submit();
      }
      return true;
    },
    viewCheckIn() {
      this.showMenu = false;
      this.showCheckinModal = true;
    },
    updateActivity(value, index) {
      this.activitiesToCreate.splice(index, 1, value);
    },
    addGoalActivity() {
      if (this.goal === null) {
        return;
      }

      const nextPropertyStatus = this.calculateInitialPropertyStatus(this.goal);

      this.activitiesToCreate.push({
        uid: undefined,
        goal: this.goal,
        current: current(this.goal, this.goal.cachedCurrent),
        message: null,
        properties: nextPropertyStatus,
        customCreatedAt: DateTime.local().toISO(),
      });
    },
    blur() {
      this.content = trimEmptyLines(this.content);
      if (this.$refs.editor !== undefined) {
        this.$refs.editor.setContent(this.content);
      }
      this.focused = false;
      if (this.allowComments && !this.messageDirty) {
        this.disabled = this.initialDisabled;
      }
    },
    submit() {
      if (this.update === null) {
        this.create();
        return;
      }
      if (this.messageEmpty && this.activitiesToCreate.length === 0) {
        this.onDeleteUpdate();
        return;
      }
      this.save();
    },
    create() {
      if (this.messageEmpty && this.activitiesToCreate.length === 0) {
        return;
      }

      this.createUpdate({
        message: this.content,
        goal: { uid: this.goal.uid },
        goalActivities: this.activitiesToCreate,
      }).then(() => {
        this.editorKey += 1;
        this.content = null;
        this.activitiesToCreate = [];
        this.$emit('created');
      }).catch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      });
    },
    save() {
      this.updateUpdate({
        ...this.update,
        message: this.content,
        goalActivities: this.activitiesToCreate,
      }).then(() => {
        this.fetchUpdatesForGoal(this.goal.uid);
        this.$emit('saved');
        this.disabled = true;
      }).catch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      });
    },
    editUpdate() {
      this.showMenu = false;
      this.disabled = false;
      if (this.allowComments) {
        this.$refs.editor.focus();
      }
    },

    deleteUpdateEntity() {
      this.deleteUpdate(this.update).then(() => {
        this.$emit('deleted');
        this.fetchUpdatesForGoal(this.goal.uid);
      }).catch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      });
    },
    onDeleteUpdate() {
      this.showMenu = false;
      const deleteMethod = this.deleteUpdateEntity;

      this.$confirm({
        title: this.$t('goalUpdateEditor.deleteUpdatePrompt'),
        okText: this.$t('general.yesDelete'),
        okType: 'danger',
        maskClosable: true,
        cancelText: this.$t('general.cancel'),
        onOk() {
          deleteMethod();
        },
      });
    },
  },
  watch: {
    update: {
      handler(newVal, oldVal) {
        if (isEqual(newVal, oldVal)) {
          return;
        }

        this.activitiesToCreate = copy(newVal.goalActivities);
      },
      deep: true,
    },
  },
  mounted() {
    this.disabled = this.initialDisabled;

    if (this.update !== null) {
      this.activitiesToCreate = copy(this.update.goalActivities);
      if (this.update.type !== updateType.checkin) {
        this.initialContent = this.update.message;
        this.content = this.initialContent;
      }
    }

    if (this.autoAddGoalActivity) {
      this.addGoalActivity();
    }
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  @import "shared/assets/scss/box-shadow";

  .goal-update-editor {
    ._content {
      display: flex;
      flex-direction: column;
      gap: .4rem;

      ._comment-container {
        display: flex;
        flex-wrap: wrap;
        gap: .4rem .6rem;
        justify-content: flex-end;
        align-items: flex-end;

        ._editor {
          flex-grow: 1;
          min-width: 0;
        }
      }

      ._activity-container {
        display: flex;
        flex-direction: column;
        gap: .6rem;
      }

      ._submit {
        display: flex;
        justify-content: flex-end;
        padding-bottom: .4rem;
      }
    }

    ._bottom-actions {
      margin-left: 3rem;
      flex-grow: 1;

      .reaction-list {
        margin-top: .6rem;
      }
    }

    ._tooltip {
      ._icon-info {
        cursor: help;
      }
    }
  }
</style>
